import { RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'vnetwork-ui'
import { EInvoiceCurrency, EInvoicePreviewItem } from '../../types'
import {
    eInvoiceContractReference,
    formatEInvoiceAmount,
} from '../../utils/e-invoice'

interface EInvoiceItemsEditorProps {
    items: EInvoicePreviewItem[]
    /** Nội dung đang nhập theo invoice_item_id */
    values: Record<number, string>
    onChange: (values: Record<number, string>) => void
    currency?: EInvoiceCurrency
    disabled?: boolean
}

/**
 * Bảng sửa nội dung dòng hàng của hoá đơn điện tử: tên dòng DC | ô nội dung | thành tiền.
 * Nút bên cạnh ô đưa nội dung về gợi ý của hệ thống. Ô trống viền đỏ (không được để trống).
 * Dưới ô là hợp đồng / phụ lục liên kết với dịch vụ của dòng: bấm để nối câu dẫn chiếu vào nội dung.
 */
const EInvoiceItemsEditor = ({
    items,
    values,
    onChange,
    currency,
    disabled,
}: EInvoiceItemsEditorProps) => {
    const { t } = useTranslation('invoice')
    const setValue = (id: number, value: string) =>
        onChange({ ...values, [id]: value })

    return (
        <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                        <th className="w-10 px-3 py-2 font-medium">#</th>
                        <th className="w-1/4 px-3 py-2 font-medium">
                            {t('e-invoice-item-name')}
                        </th>
                        <th className="px-3 py-2 font-medium">
                            {t('e-invoice-item-description')}
                        </th>
                        <th className="w-44 px-3 py-2 text-right font-medium">
                            {t('e-invoice-item-amount')}
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {items.map((item, index) => {
                        const value = values[item.invoice_item_id] ?? ''
                        return (
                            <tr key={item.invoice_item_id}>
                                <td className="px-3 py-2 align-top text-gray-500">
                                    {index + 1}
                                </td>
                                <td className="break-words px-3 py-2 align-top text-gray-500">
                                    {item.invoice_item_name}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <div className="space-y-1">
                                        <div className="flex items-start gap-1">
                                            <textarea
                                                value={value}
                                                onChange={(event) =>
                                                    setValue(
                                                        item.invoice_item_id,
                                                        event.target.value
                                                    )
                                                }
                                                rows={2}
                                                disabled={disabled}
                                                className={`w-full rounded-md border px-2 py-1 focus:outline-none ${
                                                    value.trim()
                                                        ? 'border-gray-300 focus:border-primary'
                                                        : 'border-red-400'
                                                }`}
                                            />
                                            {value.trim() !==
                                                item.suggested_description && (
                                                <Tooltip
                                                    title={t(
                                                        'e-invoice-item-description-reset'
                                                    )}
                                                >
                                                    <button
                                                        type="button"
                                                        disabled={disabled}
                                                        onClick={() =>
                                                            setValue(
                                                                item.invoice_item_id,
                                                                item.suggested_description
                                                            )
                                                        }
                                                        className="mt-1 text-gray-400 hover:text-primary"
                                                    >
                                                        <RotateCcw className="h-4 w-4" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </div>
                                        {(item.contracts ?? []).map(
                                            (contract) => {
                                                const reference =
                                                    eInvoiceContractReference(
                                                        contract
                                                    )
                                                return (
                                                    <Tooltip
                                                        key={contract.number}
                                                        title={t(
                                                            'e-invoice-item-contract-add'
                                                        )}
                                                    >
                                                        <button
                                                            type="button"
                                                            disabled={
                                                                disabled ||
                                                                value.includes(
                                                                    contract.number
                                                                )
                                                            }
                                                            onClick={() =>
                                                                setValue(
                                                                    item.invoice_item_id,
                                                                    `${value.trim()} ${reference}`.trim()
                                                                )
                                                            }
                                                            className="mr-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 hover:border-primary hover:text-primary disabled:opacity-50"
                                                        >
                                                            + {reference}
                                                        </button>
                                                    </Tooltip>
                                                )
                                            }
                                        )}
                                    </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-right align-top">
                                    {formatEInvoiceAmount(
                                        item.amount,
                                        currency
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default EInvoiceItemsEditor
